import React, { useContext } from "react"
import { Button, Div, Text } from "atomize"
import { DateTime } from "luxon"
import { motion } from "framer-motion"
import Container from "../atoms/Container"
import Divider from "../atoms/Divider"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { getLessonHours } from "../utils/billing.utils"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import { formatHoursAmount as hoursLabel } from "../utils/week.utils"
import {
	canEditLesson,
	countDistinctPartecipants,
	getLessonTypeTileColors,
	lessonTypeHasCourt,
} from "../utils/func.utils"

// Il periodo che sta guardando la barra in fondo al profilo, scritto come
// lo direbbe a voce: "1 - 30 settembre", "25 ago - 10 set".
export const periodLabel = summaryDate => {
	const start = summaryDate?.start
	const end = summaryDate?.end
	if (!start?.isValid || !end?.isValid) return null

	return start.hasSame(end, "month") && start.hasSame(end, "year")
		? `${start.toFormat("d")} - ${end.setLocale("it").toFormat("d LLLL")}`
		: `${start.setLocale("it").toFormat("d LLL")} - ${end.setLocale("it").toFormat("d LLL")}`
}

// Una lezione dell'elenco: quando era, chi c'era e quanto è durata. Si
// tocca e si apre la modifica, come una lezione nella scheda di un allievo.
// `note`, se c'è, prende il posto dei nomi: una riga sola che dice la cosa
// che conta di quella lezione (un'assenza recuperata altrove).
export function LessonRow({ lessonData, courtLabel, color, onClick, note }) {
	const start = lessonData.lessonStart?.toDate
		? DateTime.fromJSDate(lessonData.lessonStart.toDate())
		: null
	const end = lessonData.lessonEnd?.toDate
		? DateTime.fromJSDate(lessonData.lessonEnd.toDate())
		: null

	const partecipants = lessonData.partecipants ?? []
	const absents = partecipants.filter(player => player.isPresent === false).length
	const names = partecipants.map(player => player.playerName?.trim()).filter(Boolean)

	const details = [
		start && end ? `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}` : null,
		courtLabel,
		// annullata non vuol dire che mancavano tutti: non si è fatta
		lessonData.cancelled === true
			? "Annullata"
			: absents > 0
				? `${absents} ${absents === 1 ? "assente" : "assenti"}`
				: null,
	].filter(Boolean)

	return (
		<motion.div whileTap={onClick && { scale: 0.98 }}>
			<Div
				p={{ y: "0.6rem" }}
				style={{ gap: "0.75rem", cursor: onClick ? "pointer" : "default" }}
				d="flex"
				align="center"
				onClick={onClick}>
				<Div
					w="0.5rem"
					h="2.5rem"
					rounded="circle"
					bg={color}
					style={{ flexShrink: 0 }}
				/>
				<Div style={{ flex: 1, minWidth: 0 }}>
					<Text textColor={theme.colors.gunmetal} textWeight="bold">
						{start ? start.setLocale("it").toFormat("ccc d LLL") : "Data non segnata"}
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{details.join(" · ")}
					</Text>
					{note ? (
						<Text textSize="caption" textWeight="600" textColor={theme.colors.green}>
							{note}
						</Text>
					) : names.length > 0 && (
						<Text
							textSize="caption"
							textColor={theme.colors.gunmetal}
							style={{
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}>
							{names.join(", ")}
						</Text>
					)}
				</Div>
				<Text
					textColor={theme.colors.gunmetal}
					textWeight="bold"
					style={{ flexShrink: 0 }}>
					{hoursLabel(getLessonHours(lessonData))}
				</Text>
			</Div>
		</motion.div>
	)
}

// Le lezioni dietro un tassello del riepilogo: quelle di quel tipo, in quel
// periodo, quelle di cui la scheda conta le ore. Toccandone una si apre la
// modifica, come nella scheda di un allievo.
function LessonTypeLessonsModal({ closeModal, selection }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const { getLessonType, getSport } = useContext(sharedDataContext)

	const { sportID, lessonType, summaryDate } = selection ?? {}
	const lessons = selection?.lessons ?? []

	const color = getLessonTypeTileColors(getLessonType(lessonType))
	const totalHours = lessons.reduce((sum, lesson) => sum + getLessonHours(lesson), 0)
	// come nel tassello: ognuno una volta, per quante lezioni abbia fatto
	const totalPeople = countDistinctPartecipants(lessons)

	const subtitle = [getSport(sportID)?.sportName, periodLabel(summaryDate)]
		.filter(Boolean)
		.join(" · ")

	// il campo non c'è per le lezioni che non si fanno in campo, e nei dati
	// vecchi è un numero invece dell'oggetto di adesso
	const courtLabelOf = lessonData => {
		if (!lessonData.court || !lessonTypeHasCourt(getLessonType(lessonData.lessonType)))
			return null
		return `Campo ${lessonData.court.courtNumber ?? lessonData.court}`
	}

	const handleLessonClick = lessonData =>
		canEditLesson(coach, selectedSchool, lessonData)
			? () => Modal.open("editLesson", lessonData)
			: undefined

	return (
		<>
			<Container>
				<Div d="flex" align="center" m={{ b: "0.25rem" }} style={{ gap: "0.6rem" }}>
					<Div w="0.9rem" h="0.9rem" rounded="circle" bg={color.bg} />
					<Text textWeight="bold" textSize="title">
						<LessonTypeLabel
							lessonTypeID={lessonType}
							lessonType={getLessonType(lessonType)}
						/>
					</Text>
				</Div>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{subtitle}
				</Text>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
					{lessons.length} {lessons.length === 1 ? "lezione" : "lezioni"} ·{" "}
					{hoursLabel(totalHours)}
					{totalPeople > 0 &&
						` · ${totalPeople} ${totalPeople === 1 ? "persona" : "persone"}`}
				</Text>
			</Container>

			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{lessons.length === 0 ? (
						<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
							Nessuna lezione di questo tipo nel periodo
						</Text>
					) : (
						lessons.map((lesson, i) => (
							<Div key={lesson.lessonID ?? i}>
								<LessonRow
									lessonData={lesson}
									courtLabel={courtLabelOf(lesson)}
									color={color.bg}
									onClick={handleLessonClick(lesson)}
								/>
								{i < lessons.length - 1 && <Divider w="2px solid" />}
							</Div>
						))
					)}
				</Container>
			</Div>

			<Container>
				<Div d="flex" justify="center" m={{ t: "1rem" }}>
					<Button
						h="3.5rem"
						p={{ x: "2rem" }}
						bg={theme.colors.gunmetal_fill}
						textColor={theme.colors.white}
						textSize="subheader"
						rounded={theme.rounded["20px"]}
						onClick={closeModal}>
						Chiudi
					</Button>
				</Div>
			</Container>
		</>
	)
}

export default LessonTypeLessonsModal
